import { apiClient } from '../apiClient';
import { AxiosError } from 'axios';

const isDev = process.env.NODE_ENV === 'development';

export class BaseService {
  protected static async get<T>(endpoint: string): Promise<T> {
    try {
      const response = await apiClient.get<T>(endpoint);
      return response.data;
    } catch (error: any) {
      // Suppress CORS/network errors - these are expected backend configuration issues
      const isCorsError = error.code === 'ERR_NETWORK' || 
                         error.message?.includes('CORS') ||
                         error.message?.includes('Access-Control-Allow-Origin');
      if (!isCorsError && isDev) {
        console.error(`GET request failed for ${endpoint}:`, error);
      }
      throw error;
    }
  }

  protected static async post<T>(endpoint: string, data: Record<string, unknown>): Promise<T> {
    try {
      if (isDev) {
        console.log(`[API Request] POST ${endpoint}`);
        console.log('[API Request] Request Data:', JSON.stringify(data, null, 2));
      }
      const response = await apiClient.post<T>(endpoint, data);
      if (isDev) {
        console.log('[API Response] Status:', response.status);
        console.log('[API Response] Response Data:', JSON.stringify(response.data, null, 2));
      }
      return response.data;
    } catch (error: unknown) {
      // Suppress CORS/network errors
      const isCorsError = error instanceof Error && (
        (error as any).code === 'ERR_NETWORK' || 
        error.message?.includes('CORS') ||
        error.message?.includes('Access-Control-Allow-Origin')
      );
      if (!isCorsError && isDev) {
        console.error(`POST request failed for ${endpoint}:`, error);
      }
      if (isDev && error instanceof AxiosError && error.response) {
        console.error('[API Error] Status:', error.response.status);
        console.error('[API Error] Response Data:', error.response.data);

        // Log detailed validation errors if available
        if (error.response.data && error.response.data.detail) {
          if (Array.isArray(error.response.data.detail)) {
            console.error('[API Error] Validation Errors:');
            error.response.data.detail.forEach((validationError: unknown, index: number) => {
              const errorObj = validationError as {
                loc?: string[];
                msg?: string;
                message?: string;
                type?: string;
              };
              console.error(`  ${index + 1}. Field: ${errorObj.loc?.join('.') || 'unknown'}`);
              console.error(
                `     Message: ${errorObj.msg || errorObj.message || 'validation error'}`
              );
              console.error(`     Type: ${errorObj.type || 'unknown'}`);
            });
          } else {
            console.error('[API Error] Detail:', error.response.data.detail);
          }
        }
      }
      throw error;
    }
  }

  protected static async patch<T>(endpoint: string, data: Record<string, unknown>): Promise<T> {
    try {
      const response = await apiClient.patch<T>(endpoint, data);
      return response.data;
    } catch (error: any) {
      const isCorsError = error.code === 'ERR_NETWORK' ||
        error.message?.includes('CORS') ||
        error.message?.includes('Access-Control-Allow-Origin');
      if (!isCorsError && isDev) {
        console.error(`PATCH request failed for ${endpoint}:`, error);
      }
      throw error;
    }
  }

  protected static async put<T>(endpoint: string, data: Record<string, unknown>): Promise<T> {
    try {
      // Let the request go through - the interceptor will handle token addition
      // Don't check token here as it might be cleared/expired, but we should still
      // let the backend handle the authentication response
      if (isDev) {
        console.log(`[API Request] PUT ${endpoint}`);
        console.log('[API Request] Request Data:', JSON.stringify(data, null, 2));
      }
      const response = await apiClient.put<T>(endpoint, data);
      if (isDev) {
        console.log('[API Response] Status:', response.status);
        console.log('[API Response] Response Data:', JSON.stringify(response.data, null, 2));
      }
      return response.data;
    } catch (error: any) {
      // Suppress CORS/network errors
      const isCorsError = error.code === 'ERR_NETWORK' || 
                         error.message?.includes('CORS') ||
                         error.message?.includes('Access-Control-Allow-Origin');
      
      if (!isCorsError && isDev) {
        // Check if it's a 401 error and provide helpful message
        if (error.response?.status === 401) {
          const errorDetail = error.response?.data?.detail || error.response?.data?.message || 'Unauthorized';
          console.error(`PUT request failed for ${endpoint}: 401 Unauthorized - ${errorDetail}`);
        } else {
          console.error(`PUT request failed for ${endpoint}:`, error);
        }
      }
      throw error;
    }
  }

  protected static async delete(endpoint: string): Promise<void> {
    try {
      await apiClient.delete(endpoint);
    } catch (error: any) {
      // Suppress CORS/network errors
      const isCorsError = error.code === 'ERR_NETWORK' || 
                         error.message?.includes('CORS') ||
                         error.message?.includes('Access-Control-Allow-Origin');
      if (!isCorsError && isDev) {
        console.error(`DELETE request failed for ${endpoint}:`, error);
      }
      throw error;
    }
  }
}
